import type {
  AdminActivityRequest,
  AdminActivityResponse,
  AdminBackupsResponse,
  AdminCompleteProjectResponse,
  AdminCreateAssetRequest,
  AdminCreateAssetResponse,
  AdminCreateProjectRequest,
  AdminCreateProjectResponse,
  AdminCreateShareLinkRequest,
  AdminCreateShareLinkResponse,
  AdminEditAccessLinkResponse,
  AdminCreateUploadUrlRequest,
  AdminCreateUploadUrlResponse,
  AdminDeleteAssetMediaResponse,
  AdminDeleteAssetResponse,
  AdminDeleteProjectResponse,
  AdminHardDeleteProjectResponse,
  AdminExtendProjectRequest,
  AdminExtendProjectResponse,
  AdminGetProjectResponse,
  AdminListProjectsRequest,
  AdminListProjectsResponse,
  AdminProjectExportRequest,
  AdminProjectExportResponse,
  AdminProjectOverviewResponse,
  AdminRecalculateStorageResponse,
  AdminReorderAssetsRequest,
  AdminReorderAssetsResponse,
  AdminRevokeShareLinkResponse,
  AdminReissueShareLinkResponse,
  AdminRunBackupResponse,
  AdminRestoreProjectResponse,
  AdminStorageResponse,
  AdminTranscriptCommitRequest,
  AdminTranscriptCommitResponse,
  AdminTranscriptPreviewRequest,
  AdminTranscriptPreviewResponse,
  AdminUpdateAssetRequest,
  AdminUpdateAssetResponse,
  AdminUpdateProjectRequest,
  AdminUpdateProjectResponse,
  AdminUploadCompleteRequest,
  AdminUploadCompleteResponse,
  ApiErrorResponse,
  ApiHealthResponse,
  AuthErrorsResponse,
  AuthLoginRequest,
  AuthLoginResponse,
  AuthLogoutResponse,
  AuthMeResponse,
  AuthSetupRequest,
  AuthSetupResponse,
  AuthStatusResponse,
  AuthTotpDisableRequest,
  AuthTotpDisableResponse,
  AuthTotpEnableRequest,
  AuthTotpEnableResponse,
  AuthTotpSetupResponse,
  AuthTotpStatusResponse,
  MediaAssetResponse,
  ReviewCreateAnnotationRequest,
  ReviewCreateAnnotationResponse,
  ReviewCreateCommentRequest,
  ReviewCreateCommentResponse,
  ReviewDeleteAnnotationResponse,
  ReviewDeleteCommentResponse,
  ReviewFinishResponse,
  ReviewGetProjectResponse,
  ReviewIdentifyRequest,
  ReviewIdentifyResponse,
  ReviewSessionRequest,
  ReviewSessionResponse,
  ReviewUpdateAnnotationRequest,
  ReviewUpdateAnnotationResponse,
  ServiceCreateReviewRequest,
  ServiceGetReviewResponse,
  ServiceReviewResponse,
} from '../../shared/types';

export const API_FALLBACK_ERROR_MESSAGE = 'Request failed';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function queryString(values: Record<string, string | number | undefined>): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(values)) {
    if (value !== undefined) query.set(key, String(value));
  }
  const text = query.toString();
  return text ? `?${text}` : '';
}

let adminSessionExpired: (() => void) | null = null;

/**
 * Registers the one listener for an expired admin session. Without it a 401
 * from a later action only ever surfaced as a local widget error, leaving the
 * admin with no route back to the login page but a guessed reload.
 */
export function onAdminSessionExpired(handler: (() => void) | null): void {
  adminSessionExpired = handler;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: 'same-origin',
    ...init,
    headers: {
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init?.headers ?? {}),
    },
  });
  // Only the guarded admin surface: a 401 on /api/auth or /api/review is an
  // expected answer there, not a session that ran out underneath someone.
  if (response.status === 401 && path.startsWith('/api/admin')) adminSessionExpired?.();
  if (!response.ok) {
    let body: unknown;
    try {
      body = await response.json();
    } catch {
      body = undefined;
    }
    const message = (body as ApiErrorResponse | undefined)?.error ?? API_FALLBACK_ERROR_MESSAGE;
    throw new ApiError(response.status, message, body);
  }
  return (await response.json()) as T;
}

async function requestBlob(path: string): Promise<Blob> {
  const response = await fetch(path, { credentials: 'same-origin' });
  if (response.status === 401 && path.startsWith('/api/admin')) adminSessionExpired?.();
  if (!response.ok) {
    let body: unknown;
    try {
      body = await response.json();
    } catch {
      body = undefined;
    }
    throw new ApiError(
      response.status,
      (body as ApiErrorResponse | undefined)?.error ?? 'Export failed',
      body,
    );
  }
  return response.blob();
}

const json = (body: unknown): string => JSON.stringify(body);

type AdminUploadUrlRequest = Omit<AdminCreateUploadUrlRequest, 'mime_type'> & {
  mime_type: AdminCreateUploadUrlRequest['mime_type'] | 'image/jpeg' | 'image/webp';
};

interface AdminUploadUrlResponse extends AdminCreateUploadUrlResponse {
  storage: {
    used_bytes: number;
    declared_bytes: number;
    projected_bytes: number;
    ceiling_bytes: number;
  };
}

export const authApi = {
  status: () => request<AuthStatusResponse>('/api/auth/status'),
  setup: (body: AuthSetupRequest) =>
    request<AuthSetupResponse>('/api/auth/setup', { method: 'POST', body: json(body) }),
  login: (body: AuthLoginRequest) =>
    request<AuthLoginResponse>('/api/auth/login', { method: 'POST', body: json(body) }),
  logout: () => request<AuthLogoutResponse>('/api/auth/logout', { method: 'POST' }),
  me: () => request<AuthMeResponse>('/api/auth/me'),
  totpStatus: () => request<AuthTotpStatusResponse>('/api/auth/totp'),
  totpSetup: () => request<AuthTotpSetupResponse>('/api/auth/totp/setup', { method: 'POST' }),
  totpEnable: (body: AuthTotpEnableRequest) =>
    request<AuthTotpEnableResponse>('/api/auth/totp/enable', {
      method: 'POST',
      body: json(body),
    }),
  totpDisable: (body: AuthTotpDisableRequest) =>
    request<AuthTotpDisableResponse>('/api/auth/totp/disable', {
      method: 'POST',
      body: json(body),
    }),
  errors: () => request<AuthErrorsResponse>('/api/auth/errors'),
};

export const healthApi = {
  get: () => request<ApiHealthResponse>('/api/health'),
};

export const adminApi = {
  backups: () => request<AdminBackupsResponse>('/api/admin/backups'),
  runBackup: () =>
    request<AdminRunBackupResponse>('/api/admin/backups/run', { method: 'POST' }),
  listProjects: (params: AdminListProjectsRequest = {}) =>
    request<AdminListProjectsResponse>(
      `/api/admin/projects${queryString({
        status: params.status,
        cursor: params.cursor,
        limit: params.limit,
      })}`,
    ),
  createProject: (body: AdminCreateProjectRequest) =>
    request<AdminCreateProjectResponse>('/api/admin/projects', {
      method: 'POST',
      body: json(body),
    }),
  getProject: (id: string) => request<AdminGetProjectResponse>(`/api/admin/projects/${id}`),
  updateProject: (id: string, body: AdminUpdateProjectRequest) =>
    request<AdminUpdateProjectResponse>(`/api/admin/projects/${id}`, {
      method: 'PATCH',
      body: json(body),
    }),
  deleteProject: (id: string) =>
    request<AdminDeleteProjectResponse>(`/api/admin/projects/${id}`, { method: 'DELETE' }),
  restoreProject: (id: string) =>
    request<AdminRestoreProjectResponse>(`/api/admin/projects/${id}/restore`, {
      method: 'POST',
    }),
  hardDeleteProject: (id: string) =>
    request<AdminHardDeleteProjectResponse>(`/api/admin/projects/${id}/forever`, {
      method: 'DELETE',
    }),
  createAsset: (projectId: string, body: AdminCreateAssetRequest) =>
    request<AdminCreateAssetResponse>(`/api/admin/projects/${projectId}/assets`, {
      method: 'POST',
      body: json(body),
    }),
  updateAsset: (id: string, body: AdminUpdateAssetRequest) =>
    request<AdminUpdateAssetResponse>(`/api/admin/assets/${id}`, {
      method: 'PATCH',
      body: json(body),
    }),
  deleteAsset: (id: string) =>
    request<AdminDeleteAssetResponse>(`/api/admin/assets/${id}`, { method: 'DELETE' }),
  deleteAssetMedia: (id: string) =>
    request<AdminDeleteAssetMediaResponse>(`/api/admin/assets/${id}/media`, { method: 'DELETE' }),
  reorderAssets: (projectId: string, body: AdminReorderAssetsRequest) =>
    request<AdminReorderAssetsResponse>(`/api/admin/projects/${projectId}/assets/reorder`, {
      method: 'POST',
      body: json(body),
    }),
  createUploadUrl: (assetId: string, body: AdminUploadUrlRequest, signal?: AbortSignal) =>
    request<AdminUploadUrlResponse>(`/api/admin/assets/${assetId}/upload-url`, {
      method: 'POST',
      body: json(body),
      signal,
    }),
  completeUpload: (assetId: string, body: AdminUploadCompleteRequest) =>
    request<AdminUploadCompleteResponse>(`/api/admin/assets/${assetId}/upload-complete`, {
      method: 'POST',
      body: json(body),
    }),
  previewTranscript: (body: AdminTranscriptPreviewRequest) =>
    request<AdminTranscriptPreviewResponse>('/api/admin/transcripts/preview', {
      method: 'POST',
      body: json(body),
    }),
  commitTranscript: (body: AdminTranscriptCommitRequest) =>
    request<AdminTranscriptCommitResponse>('/api/admin/transcripts/commit', {
      method: 'POST',
      body: json(body),
    }),
  createShareLink: (projectId: string, body: AdminCreateShareLinkRequest) =>
    request<AdminCreateShareLinkResponse>(`/api/admin/projects/${projectId}/share-links`, {
      method: 'POST',
      body: json(body),
    }),
  createEditAccessLink: (projectId: string) =>
    request<AdminEditAccessLinkResponse>(`/api/admin/projects/${projectId}/edit-access-link`, {
      method: 'POST',
    }),
  reissueShareLink: (id: string) =>
    request<AdminReissueShareLinkResponse>(`/api/admin/share-links/${id}/reissue`, {
      method: 'POST',
    }),
  revokeShareLink: (id: string) =>
    request<AdminRevokeShareLinkResponse>(`/api/admin/share-links/${id}/revoke`, {
      method: 'POST',
    }),
  extendProject: (id: string, body: AdminExtendProjectRequest) =>
    request<AdminExtendProjectResponse>(`/api/admin/projects/${id}/extend`, {
      method: 'POST',
      body: json(body),
    }),
  completeProject: (id: string) =>
    request<AdminCompleteProjectResponse>(`/api/admin/projects/${id}/complete`, {
      method: 'POST',
    }),
  overview: (id: string) =>
    request<AdminProjectOverviewResponse>(`/api/admin/projects/${id}/overview`),
  storage: () => request<AdminStorageResponse>('/api/admin/storage'),
  recalculateStorage: (projectId: string) =>
    request<AdminRecalculateStorageResponse>(
      `/api/admin/storage/projects/${encodeURIComponent(projectId)}/recalculate`,
      { method: 'POST' },
    ),
  exportProject: (id: string, params: AdminProjectExportRequest & { actions?: string[]; reviewers?: string[] }): Promise<AdminProjectExportResponse> =>
    requestBlob(`/api/admin/projects/${id}/export${queryString({ format: params.format, actions: params.actions?.join(','), reviewers: params.reviewers?.join(',') })}`),
  activity: (params: AdminActivityRequest = {}) =>
    request<AdminActivityResponse>(
      `/api/admin/activity${queryString({
        project_id: params.project_id,
        cursor: params.cursor,
        limit: params.limit,
      })}`,
    ),
};

export const reviewApi = {
  startSession: (body: ReviewSessionRequest) =>
    request<ReviewSessionResponse>('/api/review/session', { method: 'POST', body: json(body) }),
  getProject: () => request<ReviewGetProjectResponse>('/api/review/project'),
  identify: (body: ReviewIdentifyRequest) =>
    request<ReviewIdentifyResponse>('/api/review/identify', { method: 'POST', body: json(body) }),
  createAnnotation: (body: ReviewCreateAnnotationRequest) =>
    request<ReviewCreateAnnotationResponse>('/api/review/annotations', {
      method: 'POST',
      body: json(body),
    }),
  updateAnnotation: (id: string, body: ReviewUpdateAnnotationRequest) =>
    request<ReviewUpdateAnnotationResponse>(`/api/review/annotations/${id}`, {
      method: 'PATCH',
      body: json(body),
    }),
  deleteAnnotation: (id: string) =>
    request<ReviewDeleteAnnotationResponse>(`/api/review/annotations/${id}`, {
      method: 'DELETE',
    }),
  createComment: (body: ReviewCreateCommentRequest) =>
    request<ReviewCreateCommentResponse>('/api/review/comments', {
      method: 'POST',
      body: json(body),
    }),
  deleteComment: (id: string) =>
    request<ReviewDeleteCommentResponse>(`/api/review/comments/${id}`, { method: 'DELETE' }),
  finish: () => request<ReviewFinishResponse>('/api/review/finish', { method: 'POST' }),
};

export const mediaApi = {
  url: (assetId: string): string => `/media/${encodeURIComponent(assetId)}`,
  fetch: (assetId: string, range?: string): Promise<MediaAssetResponse> =>
    fetch(`/media/${encodeURIComponent(assetId)}`, {
      credentials: 'same-origin',
      headers: range ? { Range: range } : undefined,
    }),
};

/** Worker-to-worker contract helper. Never call this from interactive UI code. */
export const serviceApi = {
  createReview: (body: ServiceCreateReviewRequest, bearer: string) =>
    request<ServiceReviewResponse>('/api/service/reviews', {
      method: 'POST',
      headers: { Authorization: `Bearer ${bearer}` },
      body: json(body),
    }),
  getReview: (id: string, bearer: string) =>
    request<ServiceGetReviewResponse>(`/api/service/reviews/${id}`, {
      headers: { Authorization: `Bearer ${bearer}` },
    }),
};
